/**
 * Per-choice editor card. Phase 1b.2c scope (complete):
 *   - Header: "Choice N" + remove button
 *   - Label input
 *   - Destination chain (uses DestinationChainEditor)
 *   - Visibility condition (uses ConditionBuilder against `visibleIf`)
 *   - Effects (uses EffectsEditor in compact mode)
 *   - Feedback chain (uses FeedbackVariantsEditor)
 *   - Objective tags (uses ObjectiveTagsEditor)
 *
 * The visibleIf field is unique among the chain-adjacent fields in that
 * it's a *single optional condition*, not a chain. ConditionBuilder handles
 * this naturally: `onChange(null)` from the builder means "unset visibleIf"
 * (always visible), and the parent passes `undefined` back via the patch.
 *
 * Section order (top → bottom): label, destination, visibility, effects,
 * feedback, objectives. Effects-then-feedback matches the evaluation order
 * (apply effects → select feedback → resolve destination → entry effects);
 * objective tags sit at the bottom as a "tagging" concern separate from
 * "what the choice does."
 */
import {
  type Choice,
  type Destination,
  type Effect,
  type FeedbackVariant,
  type ObjectiveDecl,
  type ScenarioNode,
  type VariableDecl,
} from '@branchworks/shared';
import { ConditionBuilder } from './editors/ConditionBuilder';
import { DestinationChainEditor } from './editors/DestinationChainEditor';
import { EffectsEditor } from './editors/EffectsEditor';
import { FeedbackVariantsEditor } from './editors/FeedbackVariantsEditor';
import { ObjectiveTagsEditor } from './editors/ObjectiveTagsEditor';

interface ChoiceCardProps {
  choice: Choice;
  choiceIndex: number;
  /** The id of the node this choice lives on (so its destinations can't loop to itself by default). */
  nodeId: string;
  allNodes: Record<string, ScenarioNode>;
  variables: Record<string, VariableDecl>;
  objectives: Record<string, ObjectiveDecl>;
  onPatch: (patch: Partial<Choice>) => void;
  onReplaceDestinations: (next: Destination[]) => void;
  onReplaceEffects: (next: Effect[]) => void;
  onReplaceFeedbackVariants: (next: FeedbackVariant[]) => void;
  onSetObjectiveTags: (next: string[]) => void;
  onDelete: () => void;
}

export function ChoiceCard({
  choice,
  choiceIndex,
  nodeId,
  allNodes,
  variables,
  objectives,
  onPatch,
  onReplaceDestinations,
  onReplaceEffects,
  onReplaceFeedbackVariants,
  onSetObjectiveTags,
  onDelete,
}: ChoiceCardProps) {
  return (
    <div className="p-3 rounded border border-cream-border bg-cream">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-ink-2">
          Choice {choiceIndex + 1}
        </span>
        <button
          onClick={onDelete}
          className="text-xs hover:underline text-terracotta-dark"
        >
          Remove
        </button>
      </div>

      {/* Label */}
      <input
        value={choice.label}
        onChange={(e) => onPatch({ label: e.target.value })}
        placeholder="What the learner clicks"
        className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm mb-2"
      />

      {/* Destinations */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest mb-1 text-ink-2">
          Destination
        </div>
        <DestinationChainEditor
          destinations={choice.destinations}
          allNodes={allNodes}
          currentNodeId={nodeId}
          variables={variables}
          onChange={onReplaceDestinations}
        />
      </div>

      {/* Visibility */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest mb-1 text-ink-2">
          Visibility
        </div>
        <ConditionBuilder
          condition={choice.visibleIf}
          variables={variables}
          onChange={(c) => onPatch({ visibleIf: c ?? undefined })}
          emptyLabel="Always visible"
          addLabel="Only show this choice if…"
        />
      </div>

      {/* Effects */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest mb-1 text-ink-2">
          Effects when chosen
        </div>
        <EffectsEditor
          effects={choice.effects}
          variables={variables}
          onChange={onReplaceEffects}
          compact
        />
      </div>

      {/* Feedback */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest mb-1 text-ink-2">
          Feedback shown before transition
        </div>
        <FeedbackVariantsEditor
          feedbackVariants={choice.feedbackVariants}
          variables={variables}
          onChange={onReplaceFeedbackVariants}
        />
      </div>

      {/* Objective tags */}
      <div>
        <div className="text-[10px] uppercase tracking-widest mb-1 text-ink-2">
          Objectives this choice exercises
        </div>
        <ObjectiveTagsEditor
          selectedIds={choice.objectiveTags}
          objectives={objectives}
          onChange={onSetObjectiveTags}
        />
      </div>
    </div>
  );
}
